import React, { useEffect, useState } from 'react';
import { ArrowLeft, BellRing } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectClientNotifications from '@/components/projects/ProjectClientNotifications';
import { Button } from '@/ui/components';
import '@/pages/dataGridFocus.css';
import './ProjectClientNotificationsPage.css';

type ProjectSummary = {
  ProjectId?: number | string | null;
  ProjectCode?: string | null;
  ProjectName?: string | null;
  Status?: string | null;
  IsDeleted?: boolean | number | null;
  DeletedAt?: string | null;
};

function isProjectArchived(project: ProjectSummary | null) {
  if (!project) return false;
  const status = String(project.Status || '').trim().toLowerCase();
  return status === 'archived'
    || status === 'deleted'
    || Number(project.IsDeleted ?? 0) === 1
    || Boolean(project.DeletedAt);
}

const ProjectClientNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const normalizedProjectId = Math.trunc(Number(projectId));
  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadProject() {
      if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
        setError('A valid project is required to view client updates.');
        setLoading(false);
        return;
      }

      try {
        const { azureSqlService } = await import('@/services/azureSqlService.js');
        const result = await azureSqlService.getProjectById(normalizedProjectId, { useCache: false });
        if (!cancelled) {
          if (!result) {
            setError('Project not found.');
          } else {
            setProject(result);
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load the project.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadProject();
    return () => {
      cancelled = true;
    };
  }, [normalizedProjectId]);

  const projectPath = Number.isFinite(normalizedProjectId) && normalizedProjectId > 0
    ? `/projects/${normalizedProjectId}`
    : '/projects';
  const projectLabel = project?.ProjectCode || projectId || 'Project';

  return (
    <div className="dgf-page project-client-notifications-page">
      <header className="project-client-notifications-page__header">
        <div className="project-client-notifications-page__title-group">
          <button
            type="button"
            className="project-client-notifications-page__back"
            onClick={() => navigate(projectPath)}
            aria-label="Back to project details"
          >
            <ArrowLeft size={21} aria-hidden="true" />
          </button>
          <div>
            <div className="project-client-notifications-page__eyebrow">Project {projectLabel}</div>
            <h1>Notify Progress</h1>
          </div>
        </div>
        <Button
          variant="secondary"
          iconBefore={<ArrowLeft size={16} aria-hidden="true" />}
          onClick={() => navigate(projectPath)}
        >
          Project Details
        </Button>
      </header>

      <main className="dgf-page__content project-client-notifications-page__content">
        {loading ? (
          <div className="dgf-state-card" role="status">
            <BellRing size={30} aria-hidden="true" />
            <div className="dgf-state-card__title">Loading client updates…</div>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="dgf-state-card" role="alert">
            <div className="dgf-state-card__title">Could not open client updates</div>
            <div className="dgf-notice dgf-notice--danger">{error}</div>
            <Button variant="secondary" onClick={() => navigate(projectPath)}>Back to Project</Button>
          </div>
        ) : null}

        {!loading && !error && project ? (
          <ProjectClientNotifications
            projectId={normalizedProjectId}
            archived={isProjectArchived(project)}
          />
        ) : null}
      </main>
    </div>
  );
};

export default ProjectClientNotificationsPage;
